import { Field } from 'click-controls/controls';
import { action, computed, observable } from 'mobx';

import history from 'bz-utilities/modules/networking/history/rr4';
import classNames from 'classnames';
import { ICON_POSITION, TOASTIFY_TYPE } from 'click-controls/constant/enums';
import PasswordStrengthMeter from 'click-controls/controls/form/field/field-password/PasswordStrengthMeter';
import { FidoStore } from 'core/auth/login/Store/fido-store';
import { observer } from 'mobx-react';
import React from 'react';
import HelpStore from '../Store/help-store';
import ChangePasswordFormStore from './user-password-change-form';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { WhatIsAPassKey } from 'core/auth/2fa/setup/WhatIsAPassKey';
import { Link, withRouter } from 'react-router-dom';
import z from 'z';
import { ClickOrCoreButton as Button } from '../../../../../apps/click-app/web/src/components/settings/tabs/user/user-form';

interface IProps {
  match?: any;
  resetPassword?: boolean;
  setNewPassword?: boolean;
  handleUpdateResult?: (type: string, err: any) => void;
  containerClass?: string;
  updateBtnClass?: string;
  updateBtnText?: string;
  isEditMode: boolean;
}
let verifyCode = '';

@observer
export class ChangePassword extends React.Component<IProps> {
  store = new HelpStore();

  fidoStore = new FidoStore();
  userChangePasswordStore = new ChangePasswordFormStore();
  @observable
  currentPassword: string = '';

  @observable
  newPassword: string = '';

  @observable
  confirmPassword: string = '';

  @observable
  score: number = 0;

  @observable showPassword = false;
  @observable showConfirmPassword = false;

  constructor(props: IProps) {
    super(props);
    const { code } = this.props.match?.params;
    if (code) verifyCode = code;
    if (code) {
      this.store.verifyCode(code);
    }
    if (this.props.isEditMode) return;
    if (this.props.setNewPassword) history.replace('/verify');
    if (this.props.resetPassword) history.replace('/recover');
    if (!code && !verifyCode) history.replace('/login/help');
  }

  @computed
  get canChange() {
    if (this.newPassword == this.confirmPassword && this.score > 2) {
      const { resetPassword, setNewPassword } = this.props;
      if (resetPassword || setNewPassword) {
        return true;
      } else if (this.currentPassword !== '') {
        return true;
      }
    }
    return false;
  }

  @action.bound
  toggleShowPassword(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    this.showPassword = this.showPassword ? false : true;
  }
  @action.bound
  toggleShowConfirmPassword(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    this.showConfirmPassword = this.showConfirmPassword ? false : true;
  }

  @action.bound
  updateScore(score: number) {
    this.score = score;
  }

  @action.bound
  currentPasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.currentPassword = e.target.value;
  }

  @action.bound
  newPasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.newPassword = e.target.value;
  }

  @action.bound
  confirmPasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.confirmPassword = e.target.value;
  }

  @action.bound
  changePassword(e: any) {
    const { store } = this;
    const { resetPassword, setNewPassword, handleUpdateResult, match } = this.props;
    if (resetPassword || setNewPassword) {
      const newPassword = {
        newPassword: this.newPassword,
        confirmPassword: this.confirmPassword,
        score: this.score,
        code: verifyCode
      };
      store.newPasswordObject = newPassword;
      store.changePassword(store.newPasswordObject, this.failedLogin);
      history.push('/');
    } else {
      const { handleSavePassword } = this.userChangePasswordStore;
      handleSavePassword({
        oldPassword: this.currentPassword,
        newPassword: this.newPassword,
        score: this.score
      })
        .then(() => handleUpdateResult && handleUpdateResult(TOASTIFY_TYPE.Success, null))
        .catch((err) => handleUpdateResult && handleUpdateResult(TOASTIFY_TYPE.Error, err));
    }
    e.preventDefault();
    return false;
  }

  failedLogin() {}

  render() {
    const { resetPassword, setNewPassword, containerClass, updateBtnClass, updateBtnText } =
      this.props;
    const {
      changePassword,
      newPasswordChange,
      confirmPasswordChange,
      showPassword,
      showConfirmPassword
    } = this;

    return (
      <>
        {!resetPassword && (
          <div className="my-4 grid">
            <h3 className="mb-4 text-3xl font-bold">Setup a Passkey</h3>
            <p>{z.word('TM3')} supports Passkeys, which allow Passwordless sign ins.</p>
            <Button
              color="primary"
              onClick={async () => {
                try {
                  const res = await this.fidoStore.onFidoRegister();
                  if (res) {
                    history.replace('/');
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
            >
              Setup a Passkey
            </Button>
            <WhatIsAPassKey />

            {this.fidoStore.message && (
              <>
                <details className="my-2 rounded-md border border-rose-300 bg-rose-100 p-3 text-center text-sm text-rose-600">
                  <summary>
                    <span>There was an error registering your account by Passkey</span>
                  </summary>
                  <p className="mt-2">{this.fidoStore.message}</p>
                  <p className="mt-2">{this.fidoStore.error?.toString()}</p>
                </details>
                <Link to="/login">Return to Sign in Screen</Link>
              </>
            )}
          </div>
        )}
        <form onSubmit={changePassword}>
          {resetPassword && <h3 className="mb-4 text-3xl font-bold">Change password</h3>}
          {setNewPassword && <h3 className="mb-4 text-3xl font-bold">Set new password</h3>}
          {!resetPassword && !setNewPassword && (
            <Field
              type="password"
              className="click-input"
              label="Current Password"
              placeholder="Enter your current password"
              labelClass="click-label"
              onChange={this.currentPasswordChange}
              value={this.currentPassword}
            />
          )}

          <Field
            label="New Password"
            placeholder="Enter a new password"
            type={showPassword ? 'text' : 'password'}
            icon={
              <button
                tabIndex={-1}
                onClick={this.toggleShowPassword}
                className="h-5 w-5 cursor-pointer bg-white stroke-gray-600 hover:stroke-gray-800"
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            }
            iconPosition={ICON_POSITION.Right}
            onChange={newPasswordChange}
            value={this.newPassword}
          />
          <Field
            label="Confirm New Password"
            placeholder="Confirm your new password"
            type={showConfirmPassword ? 'text' : 'password'}
            icon={
              <button
                tabIndex={-1}
                onClick={this.toggleShowConfirmPassword}
                className="h-5 w-5 cursor-pointer bg-white stroke-gray-600 hover:stroke-gray-800"
              >
                {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            }
            iconPosition={ICON_POSITION.Right}
            onChange={confirmPasswordChange}
            value={this.confirmPassword}
          />
          <PasswordStrengthMeter password={this.newPassword} updateScore={this.updateScore} />
          <Button
            color="primary"
            className={classNames(`btn ${updateBtnClass}`, {
              'disabled:opacity-50': !this.canChange
            })}
            onClick={changePassword}
            disabled={!this.canChange}
          >
            {updateBtnText || 'Update'}
          </Button>
        </form>
      </>
    );
  }
}

export default withRouter(ChangePassword);
